import React, { useState } from 'react';
import { Icon } from '@/shared/icons';
import { Select, type SelectOption } from '@/shared/components/ui/Select';
import { Button } from '@/shared/components/ui/Button';
import type { HistoryImportRun } from '@troia-v3/types';

import { useStartImportSession } from '../../hooks/useHistoryImport';

const PERIOD_OPTIONS: SelectOption[] = [
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: '180', label: 'Últimos 180 dias' },
  { value: '365', label: 'Últimos 365 dias' },
];

export interface PeriodStepProps {
  channelId: string;
  /** Chamado quando a sessão foi iniciada com sucesso — o pai guarda o `runId`. */
  onStarted: (run: HistoryImportRun) => void;
}

/**
 * Passo 1 do wizard de "Importar histórico" — escolha do período (preset) +
 * aviso de vínculo temporário do aparelho + botão que inicia a sessão.
 */
export const PeriodStep: React.FC<PeriodStepProps> = ({ channelId, onStarted }) => {
  const [periodDays, setPeriodDays] = useState('90');
  const startSession = useStartImportSession();

  const handleStart = async (): Promise<void> => {
    try {
      const run = await startSession.mutateAsync({ channelId, periodDays: Number(periodDays) });
      onStarted(run);
    } catch {
      // erro já exibido via toast global (interceptor de src/lib/api.ts)
    }
  };

  return (
    <div className="space-y-4" data-testid="period-step">
      <Select
        label="Período do histórico"
        description="Quanto tempo de conversas o WhatsApp deve buscar"
        options={PERIOD_OPTIONS}
        value={periodDays}
        onChange={setPeriodDays}
        disabled={startSession.isPending}
        data-testid="period-select"
      />

      <div
        className="rounded-lg border p-3 flex gap-2.5"
        style={{ borderColor: '#fde68a', backgroundColor: '#fffbeb' }}
        data-testid="period-step-warning"
      >
        <Icon
          name="alert-triangle"
          className="w-4 h-4 flex-shrink-0 mt-0.5"
          style={{ color: '#b45309' }}
        />
        <p className="text-xs leading-relaxed" style={{ color: '#92400e' }}>
          Um aparelho temporário será vinculado ao WhatsApp deste canal e desvinculado ao
          final. A conexão atual do canal não é afetada.
        </p>
      </div>

      <Button
        variant="primary"
        fullWidth
        loading={startSession.isPending}
        onClick={handleStart}
        data-testid="start-import-button"
      >
        Conectar e buscar histórico
      </Button>
    </div>
  );
};

PeriodStep.displayName = 'PeriodStep';
